/* ⚠️ GENERATED from app/services/contentRights.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/contentRights.ts — what Allo does about other people's music, and
// about pictures a machine made.
//
// 🟥 THREE READERS, ONE RULE. The server ENFORCES this (it is the only place
//    that can strip a sound track or take a post down), the phone and the
//    browser SHOW it. A rule the enforcing copy applies and the showing copies
//    describe differently is a rule that lies to the person it is applied to —
//    so this file is generated into allo-web and server/functions from the one
//    source (tools/gen-twins.mjs). Edit the TypeScript.
//
// ══ WHAT THIS IS NOT ═══════════════════════════════════════════════════════
// 🟥 NOTHING HERE DETECTS ANYTHING. Whether a clip contains a released song is
//    a question only a fingerprint database can answer, and it is asked of a
//    provider (server/functions/musicProvider.js). Whether a picture was made by
//    a model is a question NOBODY can answer from the pixels — every "detector"
//    is a guess with false positives, and a badge from a guess is a libel of
//    someone's real photograph. So the AI label comes from two sources that are
//    not guesses: provenance the generator itself wrote into the file (C2PA
//    Content Credentials, the IPTC digital-source-type), and the author saying
//    so. This file decides what those answers MEAN and what the person sees.
//
// ══ AND WHY IMAGES ARE NOT SCANNED FOR COPYRIGHT ═══════════════════════════
// 🟢 A photograph is copyrightable and no platform detects that automatically:
//    fingerprinting systems are for audio and video, and a reverse-image match
//    answers "seen elsewhere", not "owned by someone else" — it would flag a
//    person's own photo the moment anyone reposted it. Stills are covered by
//    the notice-and-takedown flow below, which is the part that carries the
//    legal weight for every kind of post anyway.

/** Stamped on a post once its checks have run. Bump to re-run the sweep. */
export const RIGHTS_V = 1;

/* ═══════════════════════════════════════════════════════════════════════════
   MUSIC
   ═══════════════════════════════════════════════════════════════════════════ */

/** How long each fingerprint sample is, and where in the clip they are taken. */
export const MUSIC_SAMPLE_SECONDS = 12;
/* 🟢 THREE PLACES, NOT THE START. The first seconds of a reel are a title card
   or silence often enough that one sample there is a coin toss; three samples
   spread through the clip is the difference between knowing what is playing
   and guessing. Same reasoning as the transcoder's three Gemini frames. */
export const MUSIC_SAMPLE_AT = [0.1, 0.5, 0.85];

/** A provider score below this is noise, whatever it names. (0..100) */
export const MUSIC_MIN_SCORE = 80;
/** A single sample this confident is enough on its own. */
export const MUSIC_STRONG_SCORE = 95;
/** …otherwise this many samples have to name the SAME recording. */
export const MUSIC_MIN_AGREE = 2;
const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Given one provider answer per sample (null = nothing recognised), decide.
 *
 * 🟥 ONE SAMPLE IS NOT A VERDICT unless it is very sure. A twelve-second window
 *    over a crowd, a TV in the background or a ringtone can score in the 80s
 *    against something; two windows a minute apart naming the same recording
 *    do not happen by accident. Short clips that only afford one sample get
 *    the strong threshold instead, which is the honest trade: fewer false
 *    mutes, at the cost of missing a quiet ten-second clip.
 */
export function audioVerdict(samples) {
  const list = Array.isArray(samples) ? samples : [];
  const sampled = list.length;
  const votes = new Map();
  for (const s of list) {
    if (!s || !s.id) continue;
    if (num(s.score) < MUSIC_MIN_SCORE) continue;
    const v = votes.get(s.id);
    if (!v) votes.set(s.id, {
      n: 1,
      best: s
    });else {
      v.n += 1;
      if (num(s.score) > num(v.best.score)) v.best = s;
    }
  }
  let top = null;
  for (const v of votes.values()) {
    if (!top || v.n > top.n || v.n === top.n && num(v.best.score) > num(top.best.score)) top = v;
  }
  if (!top) return {
    muted: false,
    reason: null,
    match: null,
    agreeing: 0,
    sampled
  };
  const sure = top.n >= MUSIC_MIN_AGREE || sampled <= 1 && num(top.best.score) >= MUSIC_STRONG_SCORE;
  return sure ? {
    muted: true,
    reason: 'copyright',
    match: top.best,
    agreeing: top.n,
    sampled
  } : {
    muted: false,
    reason: null,
    match: top.best,
    agreeing: top.n,
    sampled
  };
}

/** What the post document carries once the check has run. */

/** Which sample offsets fit a clip of this length. */
export function sampleOffsetsFor(durationSec) {
  const d = num(durationSec);
  if (d <= 0) return [];
  if (d <= MUSIC_SAMPLE_SECONDS) return [0];
  const out = [];
  for (const f of MUSIC_SAMPLE_AT) {
    const at = Math.max(0, Math.min(d - MUSIC_SAMPLE_SECONDS, Math.floor(d * f)));
    if (!out.some(o => Math.abs(o - at) < MUSIC_SAMPLE_SECONDS)) out.push(at);
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ALLO'S OWN POSTS ARE NOT CHECKED
   ═══════════════════════════════════════════════════════════════════════════
   🟢 THE CHECK EXISTS TO PROTECT ALLO FROM WHAT ITS USERS UPLOAD. Allo posting
      Allo's own announcement, with music Allo licensed, is the one case where
      the platform already knows the answer — and it was fingerprinting itself,
      muting its own announcements and then offering itself a dispute form.

   🟥 AND A MUTE IS NOT ONLY A BANNER. It privatises the uploaded mp4 and writes
      a silent copy over the public one, then re-encodes the whole HLS ladder
      (`force`) — which is the most likely reason an official clip that plays in
      the feed comes up black in the reels for some people: they are holding a
      manifest whose segments have just been replaced underneath them. Not
      muting it at all is what removes that, not a nicer banner.

   🟥 IT IS AN IDENTITY, NOT A PERMISSION. This exempts the account that IS the
      platform — posts carrying the official page's id. It is deliberately not
      "verified accounts" or "staff": any wider rule would be a licence for
      whoever gets that flag next.
   ═══════════════════════════════════════════════════════════════════════════ */
/** The page that is Allo. Matches OFFICIAL_PAGE_ID in the app and on the server. */
export const OFFICIAL_PAGE_ID = 'allo_official';

/** Is this post Allo speaking as itself? */
export function isOfficialPost(post) {
  if (!post) return false;
  const id = OFFICIAL_PAGE_ID;
  return String(post.pageId || '') === id || String(post.userId || '') === id;
}

/** Does this post's check need (re)running? */
export function audioCheckDue(post) {
  /* 🟢 never — the sweep skips Allo's own posts entirely, so no clip of ours is
     ever sampled, matched, muted, or re-encoded for a mute */
  if (isOfficialPost(post)) return false;
  const c = post?.audioCheck;
  if (!c) return true;
  if (num(c.v) < RIGHTS_V) return true;
  return c.status === 'pending' || c.status === 'error';
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE AI LABEL
   ═══════════════════════════════════════════════════════════════════════════ */

/* 🟢 THE IPTC VOCABULARY IS THE ONE EVERY GENERATOR WRITES. C2PA action
   assertions and the plain IPTC/XMP field both carry the same URI, so one list
   answers both — and the two values that mean "a model made this" are the only
   two that earn the badge. `algorithmicMedia` is procedural (a render, a
   fractal) and `digitalCapture` is a camera; neither is what the badge means. */
export const AI_SOURCE_GENERATED = 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia';
export const AI_SOURCE_COMPOSITE = 'http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia';
/** what the C2PA manifest box is labelled in the file */
export const C2PA_MARKER = 'c2pa';
/**
 * Read provenance out of the raw bytes of an image or a video.
 *
 * 🟥 THE RAW BYTES, BEFORE ANYTHING RE-ENCODES THEM. `sharp` and ffmpeg both
 *    drop this metadata on the floor, which is right for privacy and fatal for
 *    provenance — so this runs on the original the client uploaded, not on the
 *    variants the server makes from it.
 * 🟢 A SCAN, NOT A PARSER. The URIs are stored literally in XMP (text) and in
 *    C2PA assertions (CBOR, which stores strings verbatim), so looking for the
 *    bytes finds both without a JUMBF/CBOR decoder that would be the largest
 *    thing in this file and would still miss the next container format.
 */
export function aiProvenance(bytes) {
  if (!bytes) return {
    kind: null,
    via: null
  };
  const text = typeof bytes === 'string' ? bytes : latin1(bytes);
  const generated = text.includes(AI_SOURCE_GENERATED);
  const composite = text.includes(AI_SOURCE_COMPOSITE);
  if (!generated && !composite) return {
    kind: null,
    via: null
  };
  /* the generated value is the stronger claim; a file carrying both is generated */
  const kind = generated ? 'generated' : 'composite';
  const via = text.includes(C2PA_MARKER) ? 'c2pa' : 'iptc';
  return {
    kind,
    via
  };
}
function latin1(b) {
  /* 🟢 chunked, so a 40MB clip does not build a 40M-argument call */
  let out = '';
  const CH = 0x8000;
  for (let i = 0; i < b.length; i += CH) {
    out += String.fromCharCode.apply(null, Array.from(b.subarray(i, i + CH)));
  }
  return out;
}

/**
 * The label a post carries, from what the file said and what the author said.
 *
 * 🟢 THE AUTHOR'S WORD IS ENOUGH, AND PROVENANCE IS ENOUGH. Either alone earns
 *    the badge; neither can remove it. A file that says "a model made this"
 *    is labelled even when the author forgot the toggle — and an author who
 *    says so is believed even when the file has been through a screenshot,
 *    which strips everything. When both speak, the file's word is recorded as
 *    the source because it is the one that cannot be a misclick.
 */
export function aiLabelFrom(opts) {
  const p = opts?.provenance;
  if (p && p.kind && p.via) return {
    source: p.via,
    kind: p.kind
  };
  if (opts?.declared === true) return {
    source: 'declared',
    kind: 'generated'
  };
  return null;
}

/** Does this post show the badge? One rule for the feed, the viewer and reels. */
export function showAiBadge(post) {
  const l = post?.aiLabel;
  return !!(l && l.source && l.kind);
}

/* ═══════════════════════════════════════════════════════════════════════════
   WHAT THE PERSON SEES
   ═══════════════════════════════════════════════════════════════════════════ */

/** i18n keys, so the two clients say the same sentence in every language. */
export const STR = {
  muted: 'rights_muted',
  // "Sound removed — this video contained copyrighted music"
  mutedShort: 'rights_muted_short',
  // "Sound removed"
  aiBadge: 'rights_ai_badge',
  // "AI"
  aiBadgeLong: 'rights_ai_long',
  // "Made with AI"
  aiToggle: 'rights_ai_toggle',
  // "Made with AI" (composer)
  aiToggleHint: 'rights_ai_hint',
  // "Label this as AI-generated or AI-edited"
  dispute: 'rights_dispute',
  // "Dispute"
  disputed: 'rights_disputed',
  // "Dispute sent — we'll review it"
  report: 'rights_report',
  // "Report copyright infringement"
  takenDown: 'rights_taken_down',
  // "Removed after a copyright notice"

  /* the mute, explained — the bell row, the push, and the sheet it opens */
  mutedTitle: 'rights_muted_title',
  // "Sound removed from your video"
  mutedPush: 'rights_muted_push',
  // "We found copyrighted music in it, so it plays without sound. Tap to see why and what you can do."
  mutedNotif: 'rights_notif_muted',
  // bell row: "The sound was removed from one of your videos"
  mutedWhyTitle: 'rights_muted_why_title',
  // "Why this happened"
  mutedWhy: 'rights_muted_why',
  // "A music-recognition check matched the audio to {{track}}. …"
  mutedWhyGeneric: 'rights_muted_why_generic',
  mutedWhatTitle: 'rights_muted_what_title',
  // "What changed"
  mutedWhat: 'rights_muted_what',
  // "Your video is still up — only the sound was removed. …"
  mutedHowTitle: 'rights_muted_how_title',
  // "How to fix it"
  mutedHow: 'rights_muted_how',
  // "If you own the music or have a licence, send a dispute …"
  disputeSend: 'rights_dispute_send',
  // "Dispute — I have the rights"
  disputeOpenChat: 'rights_dispute_chat',
  // "Open the conversation with Allo"
  disputeCard: 'rights_dispute_card',
  // "Copyright dispute"
  disputeAbout: 'rights_dispute_about',
  // "About the sound removed from your video"
  reqTitle: 'rights_req_title',
  // "What we need from you"
  statusLabel: 'rights_status_label' // "Status"
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE DISPUTE, AS A CONVERSATION
   ═══════════════════════════════════════════════════════════════════════════
   🟢 A DISPUTE IS A SUPPORT TICKET, NOT A FLAG ON A POST. The first version
      stamped `audioCheck.dispute = { status: 'open' }` and put a row in the
      console's Copyright page, and that was the whole exchange: the author
      could not say what they owned, staff could not ask for it, and nothing
      told the author when a person had looked. Now filing one opens the
      author's chat with the official Allo account, writes the dispute there
      as THEIR message, and Allo answers with the list below. Staff read and
      reply from the same Inbox as every other ticket. The post, the ticket
      and the message all carry the same status, and it is the message the
      author is looking at, so that is where the live indicator lives.

   🟥 THREE STATES, AND THE OUTCOME IS A SEPARATE FACT. "waiting" (filed,
      nobody has picked it up), "assigned" (a person is on it — set when a
      staff member takes it or replies), "resolved" (a decision was made).
      Which decision — the sound came back ("released") or stays off
      ("upheld") — is `outcome`, so the pill can say "Resolved · sound
      restored" without a fourth and fifth state that the assign/close
      machinery would have to know about.

   🟢 THE OLD VOCABULARY IS STILL READ. Posts disputed before tickets existed
      carry 'open' | 'upheld' | 'released' in `status`; disputeStatusOf maps
      them, so nothing written last week renders as "unknown". */
export const DISPUTE_CATEGORY = 'copyright_dispute';
export const DISPUTE_CATEGORY_LABEL = 'Copyright dispute';
export const DISPUTE_STATUS = {
  WAITING: 'waiting',
  ASSIGNED: 'assigned',
  RESOLVED: 'resolved'
};
/** every value the server's "still open" query must match — old and new */
export const DISPUTE_OPEN_STATUSES = ['open', 'waiting', 'assigned'];
/** The one status, whatever vocabulary the document was written in. */
export function disputeStatusOf(d) {
  const s = d && d.status ? String(d.status) : '';
  if (s === 'waiting' || s === 'open') return 'waiting';
  if (s === 'assigned') return 'assigned';
  if (s === 'resolved' || s === 'upheld' || s === 'released') return 'resolved';
  return null;
}

/** What was decided, if anything yet. */
export function disputeOutcomeOf(d) {
  if (!d) return null;
  if (d.outcome === 'released' || d.outcome === 'upheld') return d.outcome;
  if (d.status === 'released' || d.status === 'upheld') return d.status;
  return null;
}

/** Is somebody still owed a decision? */
export function disputeOpen(d) {
  const s = disputeStatusOf(d);
  return s === 'waiting' || s === 'assigned';
}

/* 🟢 WHAT ALLO ASKS FOR. Ids, not sentences — each client renders them in the
   reader's language (DISPUTE_REQUIREMENT_KEY), the console in English, and
   the ticket carries the ids so a requirement added later shows on old
   tickets too. Ownership first, because it decides everything else. */
export const DISPUTE_REQUIREMENTS = ['proof', 'artist', 'release', 'name'];
export const DISPUTE_REQUIREMENT_KEY = {
  proof: 'rights_req_proof',
  // "Proof you own the music or hold a licence to use it (a licence, a receipt, or a message from the rights-holder)"
  artist: 'rights_req_artist',
  // "The artist and the title of the song"
  release: 'rights_req_release',
  // "A link to where the music is published or licensed"
  name: 'rights_req_name' // "Your full legal name, so we can record the decision"
};
/** the console's English for the same ids */
export const DISPUTE_REQUIREMENT_TEXT = {
  proof: 'Proof they own the music or hold a licence to use it — a licence, a receipt, or a message from the rights-holder',
  artist: 'The artist and the title of the song',
  release: 'A link to where the music is published or licensed (label page, Jamendo, Epidemic Sound…)',
  name: 'Their full legal name, for the record of the decision'
};
export const DISPUTE_STATUS_KEY = {
  waiting: 'rights_status_waiting',
  // "Waiting for review"
  assigned: 'rights_status_assigned',
  // "Being reviewed"
  resolved: 'rights_status_resolved' // "Resolved"
};
export const DISPUTE_OUTCOME_KEY = {
  released: 'rights_outcome_released',
  // "Sound restored"
  upheld: 'rights_outcome_upheld' // "Sound stays removed"
};

/** The i18n key the status pill shows — the outcome once there is one. */
export function disputeLabelKey(d) {
  const s = disputeStatusOf(d);
  if (!s) return null;
  if (s === 'resolved') {
    const o = disputeOutcomeOf(d);
    return o ? DISPUTE_OUTCOME_KEY[o] : DISPUTE_STATUS_KEY.resolved;
  }
  return DISPUTE_STATUS_KEY[s];
}

/** The colour the pill wears: amber while waiting, blue while a person is on it, green/red once decided. */
export function disputeTone(d) {
  const s = disputeStatusOf(d);
  if (!s) return null;
  if (s === 'resolved') return disputeOutcomeOf(d) || 'upheld';
  return s;
}

/** Whether a payload from a NEWER server is still something this client can draw. */
export function disputePayloadOk(p) {
  return !!(p && typeof p === 'object' && p.postId && disputeStatusOf(p) !== null);
}

/** Is the sound of this post gone, and why?
 *  🟥 THE ONE GATE. Every "sound removed" notice, badge and dispute button in
 *     both clients and on the server asks this and nothing else — so an
 *     official post reading `null` here removes the notice from the feed, the
 *     reels, the viewers, the profile grids and the browser at once, including
 *     on documents already stamped `audio.muted` by a check that used to run. */
export function mutedReason(post) {
  if (isOfficialPost(post)) return null;
  const a = post?.audio;
  return a && a.muted === true && a.reason === 'copyright' ? 'copyright' : null;
}

/** The author may dispute a mute once, until a person has ruled.
 *  🟥 AN UPHELD DECISION IS FINAL HERE — the thread with Allo stays open for
 *     anything more they want to send, but the button does not come back. */
export function canDispute(post, myUid) {
  if (!post || !myUid || post.userId !== myUid) return false;
  if (mutedReason(post) !== 'copyright') return false;
  const d = post?.audioCheck?.dispute;
  if (!d) return true;
  if (disputeOpen(d)) return false;
  return disputeOutcomeOf(d) === 'released';
}

/* ═══════════════════════════════════════════════════════════════════════════
   NOTICE AND TAKEDOWN
   ═══════════════════════════════════════════════════════════════════════════
   🟥 THIS IS THE PART THAT CARRIES THE WEIGHT. Detection is a courtesy; safe
      harbour (17 U.S.C. §512 in the US, and the notice-and-action duties in
      the EU's DSA) depends on PROCESS: a notice with the required elements, a
      prompt takedown, the author told why and offered a counter-notice, and a
      repeat-infringer policy that is actually applied. A platform that mutes
      songs beautifully and cannot receive a notice has the courtesy and not
      the protection. */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** What a notice is missing, if anything. Empty means it may be filed. */
export function validateNotice(n) {
  const missing = [];
  const s = v => String(v || '').trim();
  if (!s(n?.postId)) missing.push('postId');
  if (s(n?.work).length < 3) missing.push('work');
  if (s(n?.name).length < 2) missing.push('name');
  if (!EMAIL.test(s(n?.email))) missing.push('email');
  if (n?.onBehalf !== 'owner' && n?.onBehalf !== 'agent') missing.push('onBehalf');
  if (n?.goodFaith !== true) missing.push('goodFaith');
  if (n?.accurate !== true) missing.push('accurate');
  /* 🟥 THE SIGNATURE IS A NAME, AND IT MUST BE THE SAME NAME. A typed signature
     that does not match the name on the notice is not a signature of it. */
  if (s(n?.signature).length < 2 || norm(s(n?.signature)) !== norm(s(n?.name))) missing.push('signature');
  return missing;
}
const norm = v => v.toLowerCase().replace(/\s+/g, ' ').trim();
export function validateCounterNotice(c) {
  const missing = [];
  const s = v => String(v || '').trim();
  if (!s(c?.noticeId)) missing.push('noticeId');
  if (s(c?.name).length < 2) missing.push('name');
  if (!EMAIL.test(s(c?.email))) missing.push('email');
  if (s(c?.address).length < 5) missing.push('address');
  if (c?.mistaken !== true) missing.push('mistaken');
  if (c?.consent !== true) missing.push('consent');
  if (s(c?.signature).length < 2 || norm(s(c?.signature)) !== norm(s(c?.name))) missing.push('signature');
  return missing;
}

/* 🟢 §512(g)(2)(C): the material goes back up not less than 10 and not more than
   14 business days after the counter-notice unless the complainant files suit.
   Fourteen calendar days is inside that window on every calendar. */
export const COUNTER_NOTICE_WAIT_MS = 14 * 24 * 60 * 60 * 1000;

/** When a removed post may be restored after a counter-notice, absent a suit. */
export function restoreDueAt(counterNoticeAt) {
  return num(counterNoticeAt) + COUNTER_NOTICE_WAIT_MS;
}

/* ── repeat infringers ────────────────────────────────────────────────────
   🟢 THREE STRIKES IN NINETY DAYS, which is the ladder people already know
      from the platforms they came from. A strike is earned by an UPHELD
      notice — a person looked — never by the automatic mute, which is a
      courtesy and not a finding. */
export const STRIKE_LIMIT = 3;
export const STRIKE_TTL_MS = 90 * 24 * 60 * 60 * 1000;
/** How many strikes count today, and whether the account is over the line. */
export function strikeDecision(strikes, now) {
  const t = num(now) || Date.now();
  const active = (Array.isArray(strikes) ? strikes : []).filter(s => s && !s.released && num(s.at) > 0 && t - num(s.at) < STRIKE_TTL_MS).length;
  return {
    active,
    terminate: active >= STRIKE_LIMIT
  };
}

/** The field every feed already filters on when a post is gone — see
 *  adminModeration.js: a takedown MOVES the post to removedPosts, so no
 *  client needs a new filter. This is the reason recorded on the move. */
export const TAKEDOWN_REASON = 'copyright';
